import { computed, reactive, ref } from 'vue'
import { useApi } from './useApi'

export const TASK_STATUSES = ['backlog', 'todo', 'in_progress', 'review', 'done', 'cancelled']
export const TASK_STATUS_DISPLAY_ORDER = ['in_progress', 'review', 'todo', 'backlog', 'done', 'cancelled']

const STATUS_INDEX = Object.fromEntries(TASK_STATUSES.map((status, index) => [status, index]))

function normalizeId(id) {
  return id === null || id === undefined ? null : String(id)
}

function normalizeFilterId(id) {
  const normalizedId = normalizeId(id)
  return normalizedId === '' ? null : normalizedId
}

function sortTasks(items) {
  return [...items].sort((a, b) => {
    const statusDiff = (STATUS_INDEX[a.status] ?? TASK_STATUSES.length) - (STATUS_INDEX[b.status] ?? TASK_STATUSES.length)
    if (statusDiff !== 0) return statusDiff

    const positionDiff = (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER)
    if (positionDiff !== 0) return positionDiff

    return Number(a.id) - Number(b.id)
  })
}

export function useTaskStore(workspaceSlug, options = {}) {
  const api = useApi()
  const tasks = ref([])
  const projects = ref(options.projects || [])
  const moveTargets = ref(options.moveTargets || [])
  const projectFilterId = ref(null)
  const openTaskId = ref(null)
  const activeTaskDetail = ref(null)
  const heldLockTokens = reactive(new Map())
  const loading = ref(false)
  const detailLoading = ref(false)
  const error = ref('')
  const detailError = ref('')
  let detailRequestToken = 0

  const filteredTasks = computed(() => {
    const selectedProjectId = normalizeFilterId(projectFilterId.value)
    if (!selectedProjectId) return sortTasks(tasks.value)

    return sortTasks(tasks.value.filter((task) => taskProjectId(task) === selectedProjectId))
  })

  const groupedByStatus = computed(() => {
    const grouped = Object.fromEntries(TASK_STATUS_DISPLAY_ORDER.map((status) => [status, []]))

    filteredTasks.value.forEach((task) => {
      if (!grouped[task.status]) grouped[task.status] = []
      grouped[task.status].push(task)
    })

    return grouped
  })

  function tasksEndpoint(path = '') {
    return `/api/workspaces/${encodeURIComponent(workspaceSlug)}/tasks${path}`
  }

  function lockEndpointUrl(id) {
    const taskId = normalizeId(id)
    return taskId ? tasksEndpoint(`/${encodeURIComponent(taskId)}/lock`) : ''
  }

  function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  function requestHeaders(extraHeaders = {}) {
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken(),
      ...extraHeaders,
    }
  }

  async function requestJson(method, url, { body = null, headers = {} } = {}) {
    const opts = {
      method,
      headers: requestHeaders(headers),
      credentials: 'same-origin',
    }

    if (body !== null && body !== undefined) {
      opts.body = JSON.stringify(body)
    }

    const response = await fetch(url, opts)
    const data = response.status === 204 ? null : await response.json()

    if (!response.ok) {
      const requestError = new Error(data?.error || data?.errors?.join(', ') || 'Request failed')
      requestError.status = response.status
      requestError.data = data
      throw requestError
    }

    return data
  }

  function lockHeaders(taskId) {
    const token = heldToken(taskId)
    return token ? { 'X-Lock-Token': token } : {}
  }

  function setRequestError(target, requestError) {
    target.value = requestError?.message || 'Request failed'
  }

  function attachLockError(requestError) {
    const lockData = requestError?.data || {}
    requestError.locked_by_label = lockData.locked_by_label
    requestError.locked_via = lockData.locked_via
    requestError.locked_at = lockData.locked_at
    return requestError
  }

  function mergeLockState(taskId, lockData) {
    if (!taskId || !lockData) return

    const { lock_token: _lockToken, ...lockFields } = lockData
    mergeTask({ id: taskId, ...lockFields })
  }

  function heldToken(id) {
    const taskId = normalizeId(id)
    return taskId ? heldLockTokens.get(taskId) : null
  }

  function mergeTask(task) {
    if (!task?.id) return

    const taskId = normalizeId(task.id)
    const index = tasks.value.findIndex((item) => normalizeId(item.id) === taskId)

    if (index === -1) {
      tasks.value = sortTasks([...tasks.value, task])
    } else {
      Object.assign(tasks.value[index], task)
      tasks.value = sortTasks(tasks.value)
    }

    if (normalizeId(activeTaskDetail.value?.id) === taskId) {
      Object.assign(activeTaskDetail.value, task)
    }
  }

  function setProjects(nextProjects) {
    projects.value = nextProjects || []
  }

  function setMoveTargets(nextMoveTargets) {
    moveTargets.value = nextMoveTargets || []
  }

  function setProjectFilter(id) {
    projectFilterId.value = normalizeFilterId(id)
  }

  function taskProjectId(task) {
    if (!task?.project) return null
    if (task.project.id) return normalizeFilterId(task.project.id)

    const matchedProject = projects.value.find((project) => project.slug === task.project.slug)
    return normalizeFilterId(matchedProject?.id)
  }

  function replaceTaskSummaries(nextTasks) {
    const existingTasks = new Map(tasks.value.map((task) => [normalizeId(task.id), task]))

    tasks.value = sortTasks(nextTasks.map((task) => {
      const existingTask = existingTasks.get(normalizeId(task.id))
      return existingTask ? { ...existingTask, ...task } : task
    }))
  }

  async function loadTasks() {
    loading.value = true
    error.value = ''

    try {
      replaceTaskSummaries(await api.get(tasksEndpoint()))
    } catch (requestError) {
      setRequestError(error, requestError)
      throw requestError
    } finally {
      loading.value = false
    }
  }

  async function refresh() {
    try {
      replaceTaskSummaries(await api.get(tasksEndpoint()))
    } catch (requestError) {
      console.error('Failed to refresh tasks', requestError)
    }
  }

  async function loadTaskDetail(id) {
    const taskId = normalizeId(id)
    if (!taskId) return null

    const requestToken = ++detailRequestToken
    detailLoading.value = true
    detailError.value = ''

    try {
      const task = await api.get(tasksEndpoint(`/${encodeURIComponent(taskId)}`))
      mergeTask(task)

      if (requestToken === detailRequestToken && normalizeId(openTaskId.value) === taskId) {
        activeTaskDetail.value = task
      }

      return task
    } catch (requestError) {
      if (requestToken === detailRequestToken) {
        setRequestError(detailError, requestError)
      }
      throw requestError
    } finally {
      if (requestToken === detailRequestToken) {
        detailLoading.value = false
      }
    }
  }

  async function createTask(attributes) {
    error.value = ''

    try {
      const task = await api.post(tasksEndpoint(), attributes)
      mergeTask(task)
      await refresh()
      return task
    } catch (requestError) {
      setRequestError(error, requestError)
      throw requestError
    }
  }

  async function updateTask(id, attributes) {
    const taskId = normalizeId(id)
    if (!taskId) return null

    detailError.value = ''
    const previousTask = tasks.value.find((item) => normalizeId(item.id) === taskId) || activeTaskDetail.value
    const previousStatus = previousTask?.status

    try {
      const task = await requestJson('PATCH', tasksEndpoint(`/${encodeURIComponent(taskId)}`), {
        body: attributes,
        headers: lockHeaders(taskId),
      })
      mergeTask(task)

      if (previousStatus && task.status !== previousStatus) {
        await refresh()
      }

      return task
    } catch (requestError) {
      setRequestError(detailError, requestError)
      throw requestError
    }
  }

  async function deleteTask(id) {
    const taskId = normalizeId(id)
    if (!taskId) return

    detailError.value = ''

    try {
      await requestJson('DELETE', tasksEndpoint(`/${encodeURIComponent(taskId)}`), {
        headers: lockHeaders(taskId),
      })
      heldLockTokens.delete(taskId)
      tasks.value = tasks.value.filter((task) => normalizeId(task.id) !== taskId)

      if (normalizeId(activeTaskDetail.value?.id) === taskId) {
        activeTaskDetail.value = null
      }

      await refresh()
    } catch (requestError) {
      setRequestError(detailError, requestError)
      throw requestError
    }
  }

  async function moveTask(id, attributes) {
    const taskId = normalizeId(id)
    if (!taskId) return null

    detailError.value = ''

    try {
      const task = await requestJson('PATCH', tasksEndpoint(`/${encodeURIComponent(taskId)}/move`), {
        body: attributes,
        headers: lockHeaders(taskId),
      })

      if (task.workspace?.slug && task.workspace.slug !== workspaceSlug) {
        tasks.value = tasks.value.filter((item) => normalizeId(item.id) !== taskId)
        if (normalizeId(activeTaskDetail.value?.id) === taskId) {
          activeTaskDetail.value = task
        }
      } else {
        mergeTask(task)
        await refresh()
      }

      return task
    } catch (requestError) {
      setRequestError(detailError, requestError)
      throw requestError
    }
  }

  async function reorderTask({ taskId, status, position }) {
    const normalizedTaskId = normalizeId(taskId)
    error.value = ''

    try {
      const result = await requestJson('PATCH', tasksEndpoint('/reorder'), {
        body: {
          task_id: taskId,
          status,
          position,
        },
        headers: lockHeaders(normalizedTaskId),
      })
      await refresh()
      return result
    } catch (requestError) {
      setRequestError(error, requestError)
      try {
        await refresh()
      } catch (_) {
        // Keep the original reorder failure visible.
      }
      throw requestError
    }
  }

  async function lockTask(id, { locked_by_label, locked_via } = {}) {
    const taskId = normalizeId(id)
    if (!taskId) return null

    detailError.value = ''

    try {
      const response = await api.post(lockEndpointUrl(taskId), {
        locked_by_label,
        locked_via,
      })

      if (response?.lock_token) {
        heldLockTokens.set(taskId, response.lock_token)
      }

      mergeLockState(taskId, response)
      await refresh()
      return response
    } catch (requestError) {
      setRequestError(detailError, requestError)
      throw attachLockError(requestError)
    }
  }

  async function unlockTask(id) {
    const taskId = normalizeId(id)
    const token = heldToken(taskId)
    if (!taskId || !token) return null

    try {
      const response = await requestJson('DELETE', lockEndpointUrl(taskId), {
        headers: { 'X-Lock-Token': token },
      })
      mergeLockState(taskId, response)
      refresh()
      return response
    } catch (requestError) {
      console.error('Failed to release task lock', requestError)
      return null
    } finally {
      heldLockTokens.delete(taskId)
    }
  }

  async function forceUnlockTask(id) {
    const taskId = normalizeId(id)
    if (!taskId) return null

    detailError.value = ''

    try {
      const response = await api.post(tasksEndpoint(`/${encodeURIComponent(taskId)}/force_unlock`))
      heldLockTokens.delete(taskId)
      mergeLockState(taskId, response)
      await refresh()
      return response
    } catch (requestError) {
      setRequestError(detailError, requestError)
      throw attachLockError(requestError)
    }
  }

  function openTask(id) {
    openTaskId.value = normalizeId(id)
  }

  function closeTask() {
    openTaskId.value = null
    activeTaskDetail.value = null
    detailError.value = ''
  }

  return {
    statuses: TASK_STATUSES,
    displayStatuses: TASK_STATUS_DISPLAY_ORDER,
    workspaceSlug,
    tasks,
    filteredTasks,
    projects,
    moveTargets,
    projectFilterId,
    groupedByStatus,
    openTaskId,
    activeTaskDetail,
    loading,
    detailLoading,
    error,
    detailError,
    loadTasks,
    setProjects,
    setMoveTargets,
    setProjectFilter,
    refresh,
    loadTaskDetail,
    createTask,
    updateTask,
    deleteTask,
    moveTask,
    reorderTask,
    heldToken,
    lockTask,
    unlockTask,
    forceUnlockTask,
    lockEndpointUrl,
    openTask,
    closeTask,
  }
}
